import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowLeft, Download, FileText, ShieldCheck, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Prospect + customer-facing legal downloads page. Public — no auth.
export default function LegalDownloadsPage() {
  const [docs, setDocs] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/public/legal/documents`);
        setDocs(data.documents || []);
      } catch (e) {
        setErr("Could not load documents. Try again in a moment.");
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-200" data-testid="legal-downloads-page">
      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-14">
          <Link
            to="/"
            className="inline-flex items-center gap-3 text-white hover:opacity-80"
            data-testid="legal-downloads-home"
          >
            <img
              src="/prestigious-logo.png"
              alt="Prestigious Property Management Services"
              className="h-12 w-12"
            />
          </Link>
          <Link
            to="/login"
            className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white inline-flex items-center gap-1.5"
            data-testid="legal-downloads-back"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Sign in
          </Link>
        </div>

        {/* Hero */}
        <div className="mb-14 max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.28em] text-orange-400 mb-3">
            Legal · Contracts
          </div>
          <h1 className="text-4xl sm:text-5xl font-display font-semibold text-white leading-[1.05] tracking-tight">
            Our contract set
          </h1>
          <p className="text-zinc-400 mt-5 leading-relaxed">
            Read, red-line, and share. Every document below is a
            counsel-ready template — download the branded PDF or grab the
            raw Markdown source to import into your contract-management
            system.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-300 text-[11px] uppercase tracking-widest">
            <ShieldCheck className="h-3.5 w-3.5" />
            All docs v1.0 — February 2026
          </div>
        </div>

        {err && (
          <div
            className="rounded-lg border border-red-500/40 bg-red-950/40 text-red-300 px-4 py-3 mb-6"
            data-testid="legal-downloads-error"
          >
            {err}
          </div>
        )}

        {!docs && !err && (
          <div className="flex items-center gap-2 text-zinc-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading contract set…
          </div>
        )}

        {/* Documents grid */}
        {docs && docs.length > 0 && (
          <div
            className="grid sm:grid-cols-2 gap-5"
            data-testid="legal-downloads-grid"
          >
            {docs.map((d) => (
              <div
                key={d.slug}
                data-testid={`legal-doc-card-${d.slug}`}
                className="group rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.04] transition-colors p-6 flex flex-col"
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="h-10 w-10 rounded-md bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                    {d.available ? `${d.size_kb} KB` : "Coming soon"}
                  </div>
                </div>
                <div className="text-[10px] uppercase tracking-[0.24em] text-orange-400 mb-1.5">
                  {d.kicker}
                </div>
                <div className="text-lg font-display font-semibold text-white leading-tight">
                  {d.title}
                </div>
                <p className="text-sm text-zinc-400 mt-2 leading-relaxed flex-1">
                  {d.description}
                </p>
                <div className="mt-5 flex items-center gap-2">
                  <a
                    href={`${API}${d.download_url}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 border border-orange-500/50 bg-orange-500/10 text-orange-200 hover:bg-orange-500/20 hover:text-white transition-colors"
                    data-testid={`legal-doc-pdf-${d.slug}`}
                  >
                    <Download className="h-3.5 w-3.5" /> PDF
                  </a>
                  <a
                    href={`${API}${d.markdown_url}`}
                    className="inline-flex items-center gap-2 text-xs uppercase tracking-widest rounded-full px-4 py-2 border border-white/15 bg-white/[0.03] text-zinc-300 hover:bg-white/[0.08] hover:text-white transition-colors"
                    data-testid={`legal-doc-md-${d.slug}`}
                  >
                    Markdown
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer note */}
        <div className="mt-16 pt-8 border-t border-white/5 text-xs text-zinc-500 max-w-2xl leading-relaxed">
          These templates are provided for reference. Have your own counsel
          review before signing. Questions about specific clauses — or
          need a customer-specific amendment — reach out at{" "}
          <a
            href="mailto:legal@prestigiouspm.net"
            className="text-orange-400 hover:text-orange-300"
          >
            legal@prestigiouspm.net
          </a>
          .
        </div>

        <footer className="mt-10 pt-6 border-t border-white/5 text-xs text-zinc-500 flex flex-wrap gap-4">
          <Link to="/terms" className="hover:text-white" data-testid="footer-terms">
            Terms of Service
          </Link>
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">
            Privacy Policy
          </Link>
          <Link to="/legal/downloads" className="hover:text-white" data-testid="footer-downloads">
            Contract downloads
          </Link>
          <span className="ml-auto">© 2026 Prestigious HOA Portal</span>
        </footer>
      </div>
    </div>
  );
}
